import { Button, Flex, Form, Input, Switch, Typography } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useHasRole } from "hooks/useHasRole";
import React, { useCallback, useEffect, useState } from "react";

import { EditMonitorForm } from "./EditMonitorForm";
import {
  SaveSearchFormProps,
  SaveSearchFormValues,
  validateSearchTerm,
} from "./types";

export const SaveSearchForm: React.FC<SaveSearchFormProps> = ({
  initialSearchTerm,
  show,
  onCancel,
  onSaveSearch,
  startWithTemplate = false,
}) => {
  const [form] = Form.useForm<SaveSearchFormValues>();
  const [saving, setSaving] = useState(false);
  const hasRole = useHasRole(["owner", "iamOwner"]);
  const [createMonitor, setCreateMonitor] =
    useState<boolean>(startWithTemplate);

  useEffect(() => {
    form.setFieldsValue({ where: initialSearchTerm });
  }, [form, initialSearchTerm]);

  const onFinish = useCallback(
    async (values: SaveSearchFormValues) => {
      setSaving(true);

      // Validate search term
      const validationError = validateSearchTerm(values.where ?? "");
      if (validationError) {
        form.setFields([
          {
            name: "where",
            value: values.where,
            errors: [
              `Unable to save search: ${validationError}. Update the query and try again.`,
            ],
          },
        ]);
        setSaving(false);
        return;
      }

      try {
        onSaveSearch(values.label, values.where, show);
        onCancel();
      } finally {
        setSaving(false);
      }
    },
    [form, onCancel, onSaveSearch, show]
  );

  useEffect(() => {
    if (startWithTemplate) {
      setCreateMonitor(true);
    }
  }, [startWithTemplate]);

  const handleToggleCreateMonitor = useCallback(() => {
    setCreateMonitor((prev) => !prev);
  }, []);

  const handleMonitorNameChange = useCallback(
    (label: string) => {
      form.setFieldsValue({ label: label });
    },
    [form]
  );

  const handleSearchTermSync = useCallback(
    (searchTerm: string) => {
      form.setFieldsValue({ where: searchTerm });
    },
    [form]
  );

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
        <Typography.Text type="secondary">
          Create a monitor for this search?
        </Typography.Text>
        <Switch
          checked={createMonitor}
          onChange={handleToggleCreateMonitor}
          disabled={!hasRole}
        />
      </Flex>
      {!createMonitor ? (
        <Form
          form={form}
          initialValues={{ where: initialSearchTerm }}
          onFinish={onFinish}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
        >
          <Form.Item
            label="Search Name"
            name="label"
            rules={[
              {
                required: true,
                message: "Please enter a name for this search",
              },
            ]}
          >
            <Input placeholder="Enter a descriptive name" />
          </Form.Item>
          <Form.Item label="Search Term" name="where">
            <TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
          </Form.Item>
          <Form.Item style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button type="primary" htmlType="submit" loading={saving}>
              Save Search
            </Button>
          </Form.Item>
        </Form>
      ) : (
        <div style={{ marginTop: 8 }}>
          <EditMonitorForm
            hideSearchAndLabel={true}
            initialSearchTerm={form.getFieldValue("where") ?? initialSearchTerm}
            isCustom={true}
            onSubmitForm={onCancel}
            show={show}
            onMonitorNameChange={handleMonitorNameChange}
            onSearchTermChange={handleSearchTermSync}
          />
        </div>
      )}
    </>
  );
};
